import { GoogleGenAI, ApiError, type GenerateVideosOperation } from '@google/genai'
import type {
  CheckStatusResult,
  GenerationJob,
  ImageRequest,
  Model,
  ProviderAdapter,
  VideoRequest,
} from '@byokapp/types'
import { ProviderError } from '@byokapp/types'

const PROVIDER = 'google-gemini'

const MODELS: Model[] = [
  // Nano Banana — Gemini image models (generateContent + IMAGE modality)
  {
    id: 'gemini-3.1-flash-image',
    name: 'Nano Banana 2',
    type: 'image',
    provider: PROVIDER,
  },
  { id: 'gemini-3-pro-image', name: 'Nano Banana Pro', type: 'image', provider: PROVIDER },
  {
    id: 'gemini-2.5-flash-image',
    name: 'Nano Banana',
    type: 'image',
    provider: PROVIDER,
  },
  // Imagen 4 — dedicated image generation models (generateImages)
  {
    id: 'imagen-4.0-generate-001',
    name: 'Imagen 4',
    type: 'image',
    provider: PROVIDER,
    capabilities: { maxImages: 4 },
  },
  {
    id: 'imagen-4.0-ultra-generate-001',
    name: 'Imagen 4 Ultra',
    type: 'image',
    provider: PROVIDER,
    capabilities: { maxImages: 1 },
  },
  {
    id: 'imagen-4.0-fast-generate-001',
    name: 'Imagen 4 Fast',
    type: 'image',
    provider: PROVIDER,
    capabilities: { maxImages: 4 },
  },
  // Veo — video generation (ordered newest first)
  {
    id: 'veo-3.1-generate-preview',
    name: 'Veo 3.1 Preview',
    type: 'video',
    provider: PROVIDER,
    capabilities: {
      durationOptions: [4, 6, 8],
      resolutionOptions: ['720p', '1080p', '4k'],
      hasAudio: true,
    },
  },
  {
    id: 'veo-3.1-fast-generate-preview',
    name: 'Veo 3.1 Fast Preview',
    type: 'video',
    provider: PROVIDER,
    capabilities: {
      durationOptions: [4, 6, 8],
      resolutionOptions: ['720p', '1080p', '4k'],
      hasAudio: true,
    },
  },
  {
    id: 'veo-3.1-lite-generate-preview',
    name: 'Veo 3.1 Lite Preview',
    type: 'video',
    provider: PROVIDER,
    capabilities: {
      durationOptions: [4, 6, 8],
      resolutionOptions: ['720p', '1080p'],
      hasAudio: true,
    },
  },
  {
    id: 'veo-3.0-generate-001',
    name: 'Veo 3',
    type: 'video',
    provider: PROVIDER,
    // Fixed 8 s clip; no durationOptions means the field is hidden in the UI
    capabilities: { hasAudio: true },
  },
  {
    id: 'veo-2.0-generate-001',
    name: 'Veo 2',
    type: 'video',
    provider: PROVIDER,
    capabilities: { durationOptions: [5, 6, 8], hasAudio: false },
  },
]

const RESULT_TTL_MS = 10 * 60 * 1000
const OP_TTL_MS = 5 * 60 * 1000

// ─── Error mapping ────────────────────────────────────────────────────────────

function mapError(err: unknown): never {
  if (err instanceof ApiError) {
    const message = err.message
    if (err.status === 400)
      throw new ProviderError({
        code: 'VALIDATION_FAILED',
        message,
        provider: PROVIDER,
        retryable: false,
      })
    if (err.status === 401 || err.status === 403)
      throw new ProviderError({ code: 'AUTH_ERROR', message, provider: PROVIDER, retryable: false })
    if (err.status === 429)
      throw new ProviderError({
        code: 'RATE_LIMITED',
        message,
        provider: PROVIDER,
        retryable: true,
      })
    if (err.status >= 500)
      throw new ProviderError({
        code: 'PROVIDER_DOWN',
        message,
        provider: PROVIDER,
        retryable: true,
      })
    throw new ProviderError({ code: 'UNKNOWN', message, provider: PROVIDER, retryable: false })
  }
  const message = err instanceof Error ? err.message : 'An unexpected error occurred.'
  throw new ProviderError({ code: 'UNKNOWN', message, provider: PROVIDER, retryable: false })
}

// ─── Image extraction ─────────────────────────────────────────────────────────

function extractGeminiImage(result: {
  candidates?: Array<{
    content?: { parts?: Array<{ inlineData?: { mimeType?: string; data?: string } }> }
  }>
}): string {
  for (const part of result.candidates?.[0]?.content?.parts ?? []) {
    if (part.inlineData?.data) {
      return `data:${part.inlineData.mimeType ?? 'image/png'};base64,${part.inlineData.data}`
    }
  }
  throw new ProviderError({
    code: 'UNKNOWN',
    message: 'Google returned no image in the response.',
    provider: PROVIDER,
    retryable: false,
  })
}

function extractImagenImages(result: {
  generatedImages?: Array<{ image?: { imageBytes?: string } }>
}): string[] {
  const images = result.generatedImages ?? []
  if (images.length === 0)
    throw new ProviderError({
      code: 'UNKNOWN',
      message: 'Google returned no image in the response.',
      provider: PROVIDER,
      retryable: false,
    })
  return images.map((img, i) => {
    const bytes = img.image?.imageBytes
    if (!bytes)
      throw new ProviderError({
        code: 'UNKNOWN',
        message: `Google returned no image bytes for image ${i + 1}.`,
        provider: PROVIDER,
        retryable: false,
      })
    return `data:image/png;base64,${bytes}`
  })
}

// Extracts the video URI from a getVideosOperation response, handling both the
// SDK camelCase shape (generatedVideos) and the REST snake_case shape (generateVideoResponse).
function extractVideoUri(response: unknown): string | undefined {
  if (!response || typeof response !== 'object') return undefined
  const r = response as Record<string, unknown>
  const genVideos = r['generatedVideos'] as Array<{ video?: { uri?: string } }> | undefined
  const uri = genVideos?.[0]?.video?.uri
  if (uri) return uri
  const gvr = r['generateVideoResponse'] as
    | { generatedSamples?: Array<{ video?: { uri?: string } }> }
    | undefined
  return gvr?.generatedSamples?.[0]?.video?.uri
}

// Splits a data: URL into { mimeType, base64 } — returns null for non-data URLs.
function parseDataUrl(url: string): { mimeType: string; base64: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(url)
  if (!match) return null
  return { mimeType: match[1] ?? 'image/png', base64: match[2] ?? '' }
}

// ─── Adapter ──────────────────────────────────────────────────────────────────

export class GoogleGeminiAdapter implements ProviderAdapter {
  readonly provider = PROVIDER

  readonly #results = new Map<string, { value: CheckStatusResult; storedAt: number }>()
  readonly #pendingOps = new Map<
    string,
    { operation: GenerateVideosOperation; apiKey: string; enqueuedAt: number }
  >()

  #pruneStale() {
    const now = Date.now()
    for (const [id, { storedAt }] of this.#results) {
      if (now - storedAt > RESULT_TTL_MS) this.#results.delete(id)
    }
    for (const [id, { enqueuedAt }] of this.#pendingOps) {
      if (now - enqueuedAt > OP_TTL_MS) this.#pendingOps.delete(id)
    }
  }

  async generateImage(request: ImageRequest, apiKey: string): Promise<GenerationJob> {
    this.#pruneStale()
    const dataUrls = await this.#fetchImages(request, apiKey)
    const jobId = crypto.randomUUID()
    this.#results.set(jobId, {
      value: { status: 'succeeded', outputUrls: dataUrls },
      storedAt: Date.now(),
    })
    return { jobId, status: 'succeeded' }
  }

  async #fetchImages(request: ImageRequest, apiKey: string): Promise<string[]> {
    const ai = new GoogleGenAI({ apiKey })
    try {
      if (request.model.startsWith('imagen-')) {
        const result = await ai.models.generateImages({
          model: request.model,
          prompt: request.prompt,
          config: {
            numberOfImages: request.numImages ?? 1,
            aspectRatio: request.aspectRatio ?? '1:1',
          },
        })
        return extractImagenImages(result)
      }
      const result = await ai.models.generateContent({
        model: request.model,
        contents: request.prompt,
        config: {
          responseModalities: ['TEXT', 'IMAGE'],
          imageConfig: { aspectRatio: request.aspectRatio ?? '1:1' },
        },
      })
      return [extractGeminiImage(result)]
    } catch (err) {
      if (err instanceof ProviderError) throw err
      mapError(err)
    }
  }

  async generateVideo(request: VideoRequest, apiKey: string): Promise<GenerationJob> {
    this.#pruneStale()
    const ai = new GoogleGenAI({ apiKey })

    const modelDef = MODELS.find((m) => m.id === request.model)
    // Only pass durationSeconds when the model has selectable duration options
    const durationSeconds =
      modelDef?.capabilities?.durationOptions !== undefined ? request.durationSeconds : undefined
    // Only pass resolution when the model supports resolution selection
    const resolution =
      modelDef?.capabilities?.resolutionOptions !== undefined ? request.resolution : undefined

    const lastFrameParsed = request.lastFrameImageUrl
      ? parseDataUrl(request.lastFrameImageUrl)
      : null

    const videoConfig = {
      numberOfVideos: 1,
      ...(durationSeconds !== undefined && { durationSeconds }),
      ...(resolution !== undefined && { resolution }),
      aspectRatio: request.aspectRatio ?? '16:9',
      ...(lastFrameParsed && {
        lastFrame: { imageBytes: lastFrameParsed.base64, mimeType: lastFrameParsed.mimeType },
      }),
    }

    const params: Parameters<typeof ai.models.generateVideos>[0] = {
      model: request.model,
      prompt: request.prompt ?? '',
      config: videoConfig as Parameters<typeof ai.models.generateVideos>[0]['config'],
    }

    if (request.imageUrl) {
      const parsed = parseDataUrl(request.imageUrl)
      if (parsed) {
        params.image = { imageBytes: parsed.base64, mimeType: parsed.mimeType }
      }
    }

    try {
      const op = await ai.models.generateVideos(params)
      const jobId = crypto.randomUUID()
      this.#pendingOps.set(jobId, { operation: op, apiKey, enqueuedAt: Date.now() })
      return { jobId, status: 'pending' }
    } catch (err) {
      if (err instanceof ProviderError) throw err
      mapError(err)
    }
  }

  async checkStatus(jobId: string, _apiKey: string): Promise<CheckStatusResult> {
    this.#pruneStale()

    const imageEntry = this.#results.get(jobId)
    if (imageEntry !== undefined) {
      this.#results.delete(jobId)
      return imageEntry.value
    }

    const pending = this.#pendingOps.get(jobId)
    if (pending !== undefined) {
      const ai = new GoogleGenAI({ apiKey: pending.apiKey })
      let updatedOp: GenerateVideosOperation
      try {
        updatedOp = await ai.operations.getVideosOperation({ operation: pending.operation })
      } catch (err) {
        mapError(err)
      }

      if (!updatedOp.done) return { status: 'running' }

      this.#pendingOps.delete(jobId)

      if (updatedOp.error) {
        const msg = (updatedOp.error['message'] as string | undefined) ?? 'Generation failed.'
        return { status: 'failed', error: msg }
      }

      const uri = extractVideoUri(updatedOp.response)
      if (!uri) {
        const reasons = updatedOp.response?.raiMediaFilteredReasons
        if (reasons && reasons.length > 0) {
          return {
            status: 'failed',
            error: reasons[0] ?? 'Generation was blocked by safety filters.',
          }
        }
        const raiCount = updatedOp.response?.raiMediaFilteredCount
        if (raiCount && raiCount > 0) {
          return {
            status: 'failed',
            error: 'Generation was blocked by safety filters. Try a different image or prompt.',
          }
        }
        return {
          status: 'failed',
          error:
            'No video was returned. This sometimes happens due to transient audio filter issues — try generating again. If the problem persists, try a different image or prompt.',
        }
      }
      return { status: 'succeeded', outputUrls: [uri] }
    }

    throw new ProviderError({
      code: 'NOT_FOUND',
      message: `No job found with id: ${jobId}`,
      provider: PROVIDER,
      retryable: false,
    })
  }

  async listModels(_apiKey: string): Promise<Model[]> {
    return [...MODELS]
  }
}
